import { Button, Modal, TextInput, Label } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Loader from '../Common/Loader';
import { UserClockIn } from '../../Models/UserClockIn';
import { User } from '../../Models/User';
import UserApi from '../../Api/UserApi';

export default function TimesheetEditModal({
  show,
  setShow,
  selectedClockIn,
  setSelectedClockIn,
  refreshData,
  users,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  selectedClockIn: UserClockIn | null;
  setSelectedClockIn: React.Dispatch<React.SetStateAction<UserClockIn | null>>;
  refreshData: () => void;
  users: User[];
}) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

  const [formData, setFormData] = useState<UserClockIn>({
    id: 0,
    userId: null,
    timeIn: null,
    timeOut: null,
  });

  // Separate date and time fields for easier editing
  const [date, setDate] = useState<string>('');
  const [timeInTime, setTimeInTime] = useState<string>('');
  const [timeOutTime, setTimeOutTime] = useState<string>('');

  const getUserName = (userId: number | null) => {
    if (!userId || !users) return '';
    const user = users.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : '';
  };

  useEffect(() => {
    if (selectedClockIn) {
      setFormData({
        ...selectedClockIn,
        timeIn: selectedClockIn.timeIn ? selectedClockIn.timeIn : null,
        timeOut: selectedClockIn.timeOut ? selectedClockIn.timeOut : null,
      });

      // Parse datetime into separate date and time fields
      if (selectedClockIn.timeIn) {
        const timeInDate = new Date(selectedClockIn.timeIn);
        // Get date in YYYY-MM-DD format (local timezone)
        const year = timeInDate.getFullYear();
        const month = String(timeInDate.getMonth() + 1).padStart(2, '0');
        const day = String(timeInDate.getDate()).padStart(2, '0');
        setDate(`${year}-${month}-${day}`);
        
        // Get time in HH:MM format (local timezone)
        const hours = String(timeInDate.getHours()).padStart(2, '0');
        const minutes = String(timeInDate.getMinutes()).padStart(2, '0');
        setTimeInTime(`${hours}:${minutes}`);
      } else {
        setDate('');
        setTimeInTime('');
      }

      if (selectedClockIn.timeOut) {
        const timeOutDate = new Date(selectedClockIn.timeOut);
        // Use the same date if clock in exists, otherwise use clock out date
        if (!selectedClockIn.timeIn) {
          const year = timeOutDate.getFullYear();
          const month = String(timeOutDate.getMonth() + 1).padStart(2, '0');
          const day = String(timeOutDate.getDate()).padStart(2, '0');
          setDate(`${year}-${month}-${day}`);
        }
        // Get time in HH:MM format (local timezone)
        const hours = String(timeOutDate.getHours()).padStart(2, '0');
        const minutes = String(timeOutDate.getMinutes()).padStart(2, '0');
        setTimeOutTime(`${hours}:${minutes}`);
      } else {
        setTimeOutTime('');
      }
    }
  }, [selectedClockIn]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDate(e.target.value);
  };

  const handleTimeInChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTimeInTime(e.target.value);
  };

  const handleTimeOutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTimeOutTime(e.target.value);
  };

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setButtonDisabled(true);

    // Combine date and time fields into datetime strings (treating as local time)
    let timeInISO: string | null = null;
    let timeOutISO: string | null = null;

    if (date && timeInTime) {
      // Create Date object treating date+time as local time
      const [year, month, day] = date.split('-').map(Number);
      const [hours, minutes] = timeInTime.split(':').map(Number);
      const localDate = new Date(year, month - 1, day, hours, minutes, 0);
      timeInISO = localDate.toISOString();
    }

    if (date && timeOutTime) {
      // Create Date object treating date+time as local time
      const [year, month, day] = date.split('-').map(Number);
      const [hours, minutes] = timeOutTime.split(':').map(Number);
      const localDate = new Date(year, month - 1, day, hours, minutes, 0);
      timeOutISO = localDate.toISOString();
    }

    const submitData: UserClockIn = {
      ...formData,
      timeIn: timeInISO,
      timeOut: timeOutISO,
    };

    return UserApi.addUpdateUserClockIn(submitData)
      .then((res) => {
        if (res.data.success) {
          toast.success('Timesheet entry updated successfully');
          refreshData();
          onCloseModal();
        } else {
          toast.error(res.data.message || 'Failed to update timesheet entry');
        }
        setIsLoading(false);
        setButtonDisabled(false);
      })
      .catch((err) => {
        console.log(err);
        toast.error('Error updating timesheet entry');
        setIsLoading(false);
        setButtonDisabled(false);
      });
  };

  function onCloseModal() {
    setButtonDisabled(false);
    setShow(false);
    setSelectedClockIn(null);
  }

  return (
    <Modal show={show} size='lg' onClose={onCloseModal} popup>
      <Modal.Header>Edit Timesheet Entry</Modal.Header>
      <Modal.Body>
        <div className='relative'>
          {isLoading && <Loader />}
          <form onSubmit={onSubmit}>
            <div className='grid gap-4'>
              <div>
                <Label htmlFor='userName' className='block text-sm font-medium text-gray-700 mb-2'>
                  User
                </Label>
                <div className='text-base font-medium text-gray-900 py-2'>
                  {getUserName(selectedClockIn?.userId ?? null) || 'N/A'}
                </div>
              </div>
              
              <div>
                <Label htmlFor='date' className='block text-sm font-medium text-gray-700 mb-2'>
                  Date
                </Label>
                <TextInput
                  id='date'
                  name='date'
                  type='date'
                  value={date}
                  onChange={handleDateChange}
                  required
                />
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <Label htmlFor='timeIn' className='block text-sm font-medium text-gray-700 mb-2'>
                    Clock In Time
                  </Label>
                  <TextInput
                    id='timeIn'
                    name='timeIn'
                    type='time'
                    value={timeInTime}
                    onChange={handleTimeInChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor='timeOut' className='block text-sm font-medium text-gray-700 mb-2'>
                    Clock Out Time
                  </Label>
                  <TextInput
                    id='timeOut'
                    name='timeOut'
                    type='time'
                    value={timeOutTime}
                    onChange={handleTimeOutChange}
                  />
                </div>
              </div>
              <div className='flex justify-end gap-2 mt-4'>
                <Button
                  type='button'
                  color='gray'
                  onClick={onCloseModal}
                  disabled={buttonDisabled}
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  className='buttonMain'
                  disabled={buttonDisabled}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Modal.Body>
    </Modal>
  );
}